import React, { memo, useCallback, useLayoutEffect, useRef, useState } from 'react';
import ToolTip from '../ToolTip';
import { isOverflowing } from './isOverflowing';

// A text span that reveals its full contents in a tooltip when — and only when —
// CSS has clipped it with an ellipsis. The caller styles the span (and so decides
// where it clips) via `className`.
//
// Measuring happens in a layout effect rather than on mouseenter because
// react-tooltip binds its hover listeners when the <ReactTooltip> mounts. If the
// tooltip only appeared in response to mouseenter, that first enter event would
// already have been spent and the initial hover would show nothing.
function TruncatedLabel({ id, text, className }) {
  const nodeRef = useRef(null);
  const observerRef = useRef(null);
  const [truncated, setTruncated] = useState(false);

  const measure = useCallback(() => setTruncated(isOverflowing(nodeRef.current)), []);

  // A label can mount inside a `display: none` flyout (the "Add to folder" submenu),
  // where it measures 0 and so looks un-truncated. Revealing it gives the element a
  // box, which fires the observer — the one chance to catch the real width before
  // the pointer arrives.
  //
  // Observing from a ref callback rather than an effect is what keeps that working:
  // flipping `truncated` swaps this component's root from a bare span to a ToolTip
  // fragment, which remounts the span. An observer bound to the old node would never
  // fire again. React re-invokes this with the live node, and with null on unmount.
  const attachRef = useCallback((el) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    nodeRef.current = el;
    if (!el) {return;}

    measure();
    if (typeof ResizeObserver === 'undefined') {return;}

    observerRef.current = new ResizeObserver(measure);
    observerRef.current.observe(el);
  }, [measure]);

  // Changing the text does not remount the element, so re-measure on it explicitly.
  useLayoutEffect(() => {measure();}, [text, measure]);

  const label = (
    <span ref={attachRef} className={className} aria-label={truncated ? text : undefined}>
      {text}
    </span>);


  if (!truncated) {return label;}

  return (
    <ToolTip id={id} label={text} place="top">
      {label}
    </ToolTip>);

}

export default memo(TruncatedLabel);
